import { useAppStore } from "@/hooks/useAppStore"
import {
  LayoutDashboard,
  Rocket,
  Activity,
  Sliders,
  Users,
  Table,
  BarChart3,
  Settings,
  Menu,
  X,
  ChevronRight,
} from "lucide-react"
import { useNavigate, useLocation } from "react-router-dom"

const NAV = [
  { href: "/", label: "Overview", icon: LayoutDashboard },
  { href: "/simulation", label: "Flight Simulation", icon: Rocket },
  { href: "/telemetry", label: "Telemetry", icon: Activity },
  { href: "/experiments", label: "Rocket Configuration", icon: Sliders },
  { href: "/workers", label: "Workers", icon: Users },
  { href: "/runs", label: "Experiment History", icon: Table },
  { href: "/comparison", label: "Comparison", icon: BarChart3 },
  { href: "/settings", label: "Settings", icon: Settings },
] as const

export function Sidebar() {
  const { sidebarOpen, setSidebarOpen } = useAppStore()
  const location = useLocation()
  const navigate = useNavigate()

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/"
    return location.pathname.startsWith(href)
  }

  const handleClose = () => setSidebarOpen(false)

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-40 w-60 border-r border-border bg-graphite-800 flex flex-col ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      } md:relative md:translate-x-0 md:border-r md:border-border transition-transform duration-300`}
      aria-label="Main navigation"
    >
      <div className="flex h-14 shrink-0 items-center justify-between px-3 border-b border-border bg-graphite-800/80 backdrop-blur-lg">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-sm bg-cyan-500 flex items-center justify-center shadow-glow">
            <Rocket className="w-4 h-4 text-black" />
          </div>
          <span className="font-semibold text-ink tracking-tight text-sm">AeroRocket</span>
        </div>
        <button
          onClick={handleClose}
          className="md:hidden p-1.5 text-dim hover:text-ink hover:bg-surface rounded-sm transition"
          aria-label="Close menu"
        >
          <X size={20} />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
        {NAV.map((item) => {
          const active = isActive(item.href)
          const Icon = item.icon
          return (
            <button
              key={item.href}
              onClick={() => {
                navigate(item.href)
                handleClose()
              }}
              className={`flex w-full items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition ${
                active
                  ? "bg-cyan-500/10 text-cyan-400 border-r-2 border-cyan-500 shadow-glow"
                  : "text-muted hover:text-ink hover:bg-surface/50"
              }`}
            >
              <Icon size={16} className={active ? "text-cyan-400" : "text-dim"} />
              <span>{item.label}</span>
              {active && <ChevronRight size={14} className="ml-auto text-dim" />}
            </button>
          )
        })}
      </nav>

      <div className="shrink-0 border-t border-border p-3">
        <div className="flex items-center gap-2 px-1">
          <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse-slow" />
          <span className="text-[0.625rem] text-dim">Systems nominal</span>
        </div>
      </div>
    </aside>
  )
}

export function SidebarTrigger() {
  const { sidebarOpen, setSidebarOpen } = useAppStore()
  return (
    <button
      className="p-2 text-dim hover:text-ink md:hidden"
      onClick={() => setSidebarOpen(!sidebarOpen)}
      aria-label="Open navigation"
    >
      <Menu size={20} />
    </button>
  )
}
